import { useState, useEffect, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import LoadingButton from '../../Components/LoadingButton';
import Modal from '../../Components/Modal';
import Table from '../../Components/Table';
import FormAutocomplete from '../../Components/forms/FormAutocomplete';
import FormSwitch from '../../Components/forms/FormSwitch';
import FormTextbox from '../../Components/forms/FormTextbox';

import { ContentContainer, ContentBox, TableBox } from '../../theme';
import {
  getConceptGroups,
  createConceptGroup,
  editConceptGroup,
  getDepartments,
} from '../../api/services';
import { useModal } from '../../hooks';
import { CONCEPTGROUP_COLUMNS } from '../../constants/tables/columns';
import { CONCEPTGROUP_INITIAL_VALUES } from '../../constants/forms/initialValues';
import {
  CONCEPTGROUP_EDIT_SCHEMA,
  CONCEPTGROUP_ADD_SCHEMA,
} from '../../constants/forms/schemas';
import useAuth from '../../context/useAuth';

const ConceptGroups = () => {
  const [conceptGroups, setConceptGroups] = useState(null);
  const [editData, setEditData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query] = useState({ isActive: true });

  const { setUser } = useAuth();

  const [open, setOpen] = useModal({ ref: false });

  const fetchData = useCallback(async () => {
    try {
      const res = await getConceptGroups();
      setConceptGroups(res);
      setLoading(false);
    } catch (error) {
      if (error.status === 401) setUser(null);
      toast.error(error.message);
    }
  }, [setUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditRequest = data => {
    setEditData(data.row);
    setOpen(true);
  };

  const handleOpen = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  return (
    <>
      <ContentContainer maxWidth="xl">
        <ContentBox>
          <TableBox>
            <Box sx={{ alignSelf: 'flex-end' }}>
              <Button
                disabled={loading}
                endIcon={<AddIcon />}
                onClick={handleOpen}
                variant="contained"
              >
                Nuevo
              </Button>
            </Box>

            <Box sx={{ height: '100%' }}>
              <Table
                columns={CONCEPTGROUP_COLUMNS(handleEditRequest)}
                loading={loading}
                rows={conceptGroups}
              />
            </Box>
          </TableBox>
        </ContentBox>
      </ContentContainer>

      {/* New/edit Modal */}
      <Modal
        disabled={loading}
        onClose={handleClose}
        open={open}
        title={`${editData ? 'Editar' : 'Nuevo'} concepto`}
        width="sm"
      >
        <Formik
          initialValues={editData || CONCEPTGROUP_INITIAL_VALUES}
          onSubmit={async values => {
            try {
              setLoading(true);

              const data = {
                ...values,
                department: values.department._id,
                account: values.account || null,
              };

              editData
                ? await editConceptGroup(data)
                : await createConceptGroup(data);
              toast.success(
                `Grupo ${editData ? 'editado' : 'creado'} con éxito.`
              );

              await fetchData();
              handleClose();
            } catch (error) {
              setLoading(false);

              if (error.status === 409) {
                setError(error.message);
                return;
              }

              if (error.status === 401) setUser(null);
              toast.error(error.message);
            }
          }}
          validationSchema={
            editData ? CONCEPTGROUP_EDIT_SCHEMA : CONCEPTGROUP_ADD_SCHEMA
          }
        >
          {props => (
            <Form>
              <Grid container spacing={2} sx={{ p: 2, mb: 4 }}>
                {error && (
                  <Grid item xs={12}>
                    <Alert
                      onClose={() => setError(null)}
                      severity="error"
                      sx={{
                        mb: 2,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'error.dark',
                      }}
                    >
                      {error}
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormTextbox
                    disabled={loading}
                    label="Grupo"
                    name="conceptGroupName"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormTextbox
                    disabled={loading}
                    label="Cuenta"
                    name="account"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormAutocomplete
                    disabled={loading}
                    label="Departamento"
                    name="department"
                    property="departmentName"
                    query={query}
                    source={getDepartments}
                  />
                </Grid>

                {editData && (
                  <Grid item xs={12}>
                    <FormSwitch
                      disabled={loading}
                      name="isActive"
                      offLabel="Inactivo"
                      onLabel="Activo"
                    />
                  </Grid>
                )}
              </Grid>

              {/* Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  px: 2,
                }}
              >
                <Button color="error" disabled={loading} onClick={handleClose}>
                  Cancelar
                </Button>
                <LoadingButton
                  icon={editData ? <EditIcon /> : <AddIcon />}
                  loading={loading}
                  text={editData ? 'Editar' : 'Guardar'}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default ConceptGroups;
